<template>
  <!-- In a scope, hide weeks whose tasks were all filtered out. -->
  <div v-if="!isScopedEmpty" v-hotkey="shortcuts" class="upcoming-week">
    <div class="week-header" :class="{ 'no-batch': !unscheduledTaskIds }">
      <span class="title">{{ weekTitle }}</span>
    </div>
    <div v-if="unscheduledTaskIds" class="batch-list">
      <div class="vertical-line">
        <div class="line" />
      </div>
      <TaskList
        :actions="{
          assignee: true,
          open: true,
          postpone: true,
          hierarchy: true,
          schedule: {
            able: true,
            type: 'long',
          },
          notes: true,
          project: true,
          space: false,
        }"
        :task-ids="unscheduledTaskIds"
        :selected-task-id="selectedTaskId"
        :is-editing="isEditing"
        :pagination="showAll ? undefined : 25"
        @quit-editing="quitEditing"
        @set-selected-task-el="(el) => setSelectedTaskEl(el)"
        @task-click="(id) => onTaskClick(id)"
      />
    </div>
    <div
      v-for="{ taskIds, day } in scheduled"
      :key="`upcoming-${week.year}-${week.n}-${getDayNumber(day)}`"
      class="scheduled"
    >
      <div class="scheduled-header">
        <span class="date">{{ getDayNumber(day) }}</span>
        <span class="day">{{ getDayTitle(day) }}</span>
      </div>
      <div class="scheduled-list">
        <div class="vertical-line">
          <div class="line" />
        </div>
        <div>
          <TaskList
            :actions="{
              assignee: true,
              open: true,
              postpone: true,
              hierarchy: true,
              schedule: {
                able: true,
                type: 'long',
              },
              notes: true,
              project: true,
              space: false,
            }"
            :task-ids="taskIds"
            :selected-task-id="selectedTaskId"
            :is-editing="isEditing"
            :pagination="showAll ? undefined : 25"
            @quit-editing="quitEditing"
            @set-selected-task-el="(el) => setSelectedTaskEl(el)"
            @task-click="(id) => onTaskClick(id)"
          />
        </div>
      </div>
    </div>
    <div v-if="!(weekIndex === weeksNumber - 1)" class="separator" />
  </div>
</template>
<script lang="ts">
import { format } from "date-fns";
import { computed, defineComponent, PropType, toRefs, watch } from "vue";

import { Batch, TaskId, Week } from "@ctrl/controller-rpc";

import { getDateFromWeek } from "@/components/dates";
import * as inject from "@/components/inject";
import { UseSettings } from "@/components/settings/controller/settings";
import { useTaskList } from "@/components/task/useTaskList";
import type { TaskScope } from "@/components/tasks/taskScope";
import { bindAsync, useReleaser } from "@/ref";

import TaskList from "./TaskList.vue";

export default defineComponent({
  components: {
    TaskList,
  },
  props: {
    // When set, display this batch instead of fetching the personal one from
    // the controller (e.g. the org Tasks screen computes batches from the
    // workspace's rows).
    batch: {
      type: Object as PropType<Batch>,
      required: false,
    },
    containerEl: { type: Object as PropType<HTMLElement> },
    isUpcomingSelected: { type: Boolean, required: true },
    // Bypass the lists' pagination and render every task, e.g. while the
    // host's find bar is open so the search covers the whole week.
    showAll: { type: Boolean, default: false },
    // When set, restrict the lists to the scope's tasks.
    taskScope: {
      type: Object as PropType<TaskScope>,
      required: false,
    },
    taskListEl: { type: Object as PropType<HTMLDivElement>, required: false },
    week: { type: Object as PropType<Week>, required: true },
    weekIndex: { type: Number, required: true },
    weeksNumber: { type: Number, required: true },
  },
  emits: {
    unselectAllTasks: () => true,
    "update-lengths": (_nbr: number) => true,
  },
  async setup(props, { emit }) {
    const {
      containerEl,
      isUpcomingSelected,
      taskListEl,
      week,
      weekIndex,
      weeksNumber,
    } = toRefs(props);
    const controller = inject.controller();
    const console = inject.consoleModal();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const { isWeekStartOnSunday } = UseSettings.getInstance();
    const locale = inject.locale();
    const contextMenu = inject.contextMenu();
    const releaser = useReleaser();
    // With a `batch` prop, use it as-is; otherwise fetch the personal batch
    // of the week from the controller.
    const fetchedBatch =
      props.batch === undefined
        ? // fetchedBatch: Ref<Batch | null | undefined>
          await releaser.add(
            bindAsync(week, (week) =>
              Promise.resolve(controller.task.batch(week))
            )
          )
        : undefined;
    const batch = computed<Batch | null | undefined>(
      () => props.batch ?? fetchedBatch?.value
    );
    const taskIds = computed<TaskId[]>(() => {
      const b = batch.value;
      return b
        ? [...b.unscheduled, ...b.scheduled.flatMap((day) => day.todo)]
        : [];
    });

    // Scoped view of the week's tasks: the scoped ids drive both the flat
    // selection list and the per-day filtering below (set membership).
    const displayedTaskIds = props.taskScope
      ? releaser.add(props.taskScope.scopedTaskIds(taskIds))
      : computed(() => taskIds.value);
    const scopedIdSet = computed<Set<string> | undefined>(() =>
      props.taskScope ? new Set(displayedTaskIds.value ?? []) : undefined
    );
    const inScope = (ids: TaskId[]): TaskId[] =>
      scopedIdSet.value ? ids.filter((id) => scopedIdSet.value?.has(id)) : ids;

    releaser.defer(
      watch(
        () => displayedTaskIds.value?.length,
        (length) => {
          if (length !== undefined) {
            emit("update-lengths", length);
          }
        },
        { immediate: true }
      )
    );

    const unscheduledTaskIds = computed<TaskId[] | undefined>(() => {
      const unscheduled = inScope(batch.value?.unscheduled ?? []);
      return unscheduled.length > 0 ? unscheduled : undefined;
    });
    const scheduled = computed<{ day: Date; taskIds: TaskId[] }[] | undefined>(
      () =>
        batch.value?.scheduled
          .map((day, index) => ({
            day: getDateFromWeek(week.value, index, isWeekStartOnSunday.value),
            taskIds: inScope(day.todo),
          }))
          .filter((day) => day.taskIds.length > 0)
    );

    const weekTitle = computed<string>(() =>
      locale.value.formatWeek(week.value)
    );
    const getDayNumber = (date: Date): string => format(date, "d");
    const getDayTitle = (date: Date): string => format(date, "EEEE");
    const isSelectable = computed(() => ({
      withUp:
        weekIndex.value === weeksNumber.value - 1 && !isUpcomingSelected.value,
      withDown: weekIndex.value === 0 && !isUpcomingSelected.value,
    }));
    const {
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      // Up/down navigation stays within the week: at its first/last task the
      // keypress keeps the current selection (selectUp/selectDown no-op at
      // the bounds) instead of jumping to a sibling week.
      shortcuts,
      taskListEl: el,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(
      displayedTaskIds,
      {
        controller,
        console,
        contextMenu,
        isModalShown,
        isInputFocused,
      },
      {
        isSelectable,
        containerEl,
      }
    );
    releaser.defer(watch(taskListEl, (v) => (el.value = v)));
    releaser.defer(releaseTaskList);

    const isWeekSelected = computed<boolean>(() =>
      displayedTaskIds.value && selectedTaskId.value
        ? displayedTaskIds.value.includes(selectedTaskId.value)
        : false
    );

    const isScopedEmpty = computed<boolean>(
      () =>
        props.taskScope !== undefined && displayedTaskIds.value?.length === 0
    );

    const onTaskClick = (taskId: string) => {
      emit("unselectAllTasks");
      selectTaskById(taskId);
    };
    return {
      getDayNumber,
      getDayTitle,
      isEditing,
      isScopedEmpty,
      isWeekSelected, // Exposed
      onTaskClick,
      quitEditing,
      scheduled,
      selectedTaskId,
      setSelectedTaskEl,
      shortcuts,
      unscheduledTaskIds,
      unselectTask,
      weekTitle,
    };
  },
});
</script>
<style scoped lang="scss">
.upcoming-week {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  flex: 1;
  .unselect-area {
    display: flex;
    flex: 1;
  }
  .vertical-line {
    height: 100%;
    .line {
      margin: 2px 0;
      height: 100%;
      width: 1px;
      @include themed() {
        background: t($grey-15);
      }
    }
  }
  .week-header {
    display: flex;
    flex-direction: row;
    &.no-batch {
      margin-bottom: 16px;
    }
    .title {
      @extend %font-h3;
    }
  }
  .batch-list {
    margin-bottom: 8px;
    padding-inline-start: 18px;
    position: relative;
  }
  .scheduled {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    margin-bottom: 8px;
    .scheduled-header {
      display: flex;
      flex-direction: row;
      column-gap: 16px;
      padding-inline-start: 34px;
      position: relative;
      .day {
        font-size: 15px;
        line-height: 18px;
        font-weight: 500;
      }
      .date {
        @extend %font-smooth;

        font-size: 15px;
        line-height: 18px;
        font-weight: 400;
        @include themed() {
          color: t($grey-40);
        }
      }
    }
    .scheduled-list {
      padding-inline-start: 18px;
      position: relative;
    }
  }
  .separator {
    width: 100%;
    margin-top: 8px;
    height: 1px;
    @include themed() {
      background: t($bg-separator);
    }
  }
  .vertical-line,
  .scheduled-header .date {
    display: flex;
    flex-direction: column;
    position: absolute;
    inset-inline-start: 0;
    width: 18px;
    align-items: center;
  }
}
</style>
